
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute = ({ children, allowedRoles = [] }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const { session, isLoading: sessionLoading } = useSessionContext();
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkUserRole = async () => {
      console.log("ProtectedRoute: Checking user role...");
      console.log("Session:", session);
      console.log("Allowed roles:", allowedRoles);

      if (!session?.user) {
        console.log("No session found, redirecting to signin");
        toast({
          title: "Authentication required",
          description: "Please sign in to access this page",
          variant: "destructive",
        });
        navigate('/signin');
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();

        console.log("Profile data:", profile);
        console.log("Profile error:", error);

        if (error) {
          console.error('Error fetching profile:', error);
          toast({
            title: "Error",
            description: "Could not verify user role",
            variant: "destructive",
          });
          navigate('/signin');
          return;
        }

        // If allowedRoles includes "*", then access is granted regardless of role
        if (allowedRoles.includes("*")) {
          console.log("Public route, access granted");
          setIsLoading(false);
          return;
        }

        if (!profile?.role || !allowedRoles.includes(profile.role)) {
          console.log("Access denied - User role:", profile?.role);
          toast({
            title: "Access denied",
            description: "You don't have permission to access this page",
            variant: "destructive",
          });
          
          // Redirect based on user role if they're trying to access a forbidden page
          if (profile?.role === 'borrower') {
            navigate('/borrower/dashboard');
          } else if (profile?.role === 'lender') {
            navigate('/lender/dashboard');
          } else if (profile?.role === 'vendor') {
            navigate('/vendor/dashboard');
          } else if (profile?.role === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/signin');
          }
          return;
        }

        console.log("Access granted - User role:", profile.role);
        setIsLoading(false);
      } catch (error) {
        console.error('Error in protected route:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
        navigate('/signin');
      }
    };

    if (!sessionLoading) {
      checkUserRole();
    }
  }, [session, sessionLoading, navigate, allowedRoles, toast]);

  if (sessionLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
